import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { getApiDomain as getApiDomainFromConstants } from "@/lib/constants/api"
import { parseApiDate } from "@/lib/datetime"

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (THB, USD, etc.)
 * @param locale - Locale for formatting (default: th-TH)
 */
export function formatCurrency(
  amount: number,
  currency: string = "THB",
  locale: string = "th-TH"
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format number with thousand separators
 * @param num - The number to format
 * @param decimals - Number of decimal places
 */
export function formatNumber(num: number, decimals: number = 0): string {
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

/**
 * Format date for display
 * @param date - Date to format
 * @param locale - Locale (th, en, etc.)
 * @param options - Intl.DateTimeFormat options
 */
export function formatDate(
  date: Date | string,
  locale: string = "th",
  options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  }
): string {
  const d = typeof date === "string" ? parseApiDate(date) : date
  const localeMap: Record<string, string> = {
    th: "th-TH",
    en: "en-US",
    lo: "lo-LA",
    km: "km-KH",
    zh: "zh-CN",
  }
  
  return new Intl.DateTimeFormat(localeMap[locale] || "th-TH", options).format(d)
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param date - Date to format
 * @param locale - Locale for formatting
 */
export function formatRelativeTime(
  date: Date | string,
  locale: string = "th"
): string {
  const d = typeof date === "string" ? parseApiDate(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })

  if (diffDays > 0) return rtf.format(-diffDays, "day")
  if (diffHours > 0) return rtf.format(-diffHours, "hour")
  if (diffMins > 0) return rtf.format(-diffMins, "minute")
  return rtf.format(-diffSecs, "second")
}


/**
 * API domain for normalizing relative image URLs.
 * Derived from NEXT_PUBLIC_API_URL env var to support multiple deployments.
 */
function getApiDomain(): string {
  return getApiDomainFromConstants()
}
// Computed once at module load. NEXT_PUBLIC_ vars are build-time on client, startup-time on server.
const API_DOMAIN = getApiDomain()

/**
 * Normalize image/asset URL from API response
 * - If URL is absolute (starts with http), returns as-is
 * - If URL is relative, prepends API domain
 * - If null/undefined, returns null
 *
 * @param url - URL from API response (may be relative)
 * @returns Normalized absolute URL or null
 */
export function normalizeApiImageUrl(url: string | undefined | null): string | null {
  if (!url) return null

  const trimmed = url.trim()
  if (!trimmed) return null

  // Already absolute URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }

  // Protocol-relative URL
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`
  }

  // Data URLs (base64/svg)
  if (trimmed.startsWith('data:')) {
    return trimmed
  }

  // Relative URL - prepend API domain
  const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return `${API_DOMAIN}${normalizedPath}`
}

const DEFAULT_QUICK_AMOUNT_STEPS = [100, 300, 500, 1000, 2000, 5000, 10000, 20000, 50000]

/**
 * Generate quick amount presets within a min/max range
 * @param min - Minimum amount
 * @param max - Maximum amount
 * @param steps - Custom step values (default: 100-50000)
 * @param limit - Max number of amounts to return (default: 5)
 */
export function generateQuickAmounts(
  min: number,
  max: number,
  steps: number[] = DEFAULT_QUICK_AMOUNT_STEPS,
  limit: number = 5,
): number[] {
  const amounts: number[] = []

  for (const step of steps) {
    if (step >= min && step <= max) {
      amounts.push(step)
    }
    if (amounts.length >= limit) break
  }

  if (amounts.length === 0) {
    amounts.push(min)
    const midPoint = Math.round((min + max) / 2)
    if (midPoint !== min && midPoint !== max) {
      amounts.push(midPoint)
    }
    if (max !== min) {
      amounts.push(max)
    }
  }

  return amounts
}
